<template lang="pug">
    div(class="_col", :style="style")
        slot
</template>

<script>
    /**
     * A column in the grid layout system. Intended to be a child of `ib-row`.
     */
    export default {
        name: 'ib-col',

        props: {
            /**
             * Column width in any CSS units, or `auto` to use all remaining space. This is an
             * alternative to the `.[s-xxxl][1-12]` classes.
             */
            width: String
        },

        computed: {
            style() {
                const width = this.width;
                return width == null ? null : {
                    flexBasis: width,
                    flexGrow: width == 'auto' ? 1 : null,
                    maxWidth: width
                };
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._col {
        max-width: 100%;
        max-height: 100%;
        background-clip: padding-box;
        flex: 0 0 100%;

        &.flex {
            display: flex;
            flex-direction: column;
        }
    }

    .gen(@name) {
        .gen-col(@n, @i: 1) when (@i <= @n) {
            @perc: (@i * 100% / @n);
            .@{name}@{i} {
                flex-basis: @perc;
                max-width: @perc;
            }
            .offset-@{name}@{i} {
                margin-left: @perc;
            }
            .gen-col(@n, (@i + 1));
        }
        .gen-col(@grid-cols);
    }

    .gen(s);

    @media screen and (min-width:@break-m) {
        .gen(m);
    }

    @media screen and (min-width:@break-l) {
        .gen(l);
    }

    @media screen and (min-width:@break-xl) {
        .gen(xl);
    }

    @media screen and (min-width:@break-xxl) {
        .gen(xxl);
    }

    @media screen and (min-width:@break-xxxl) {
        .gen(xxxl);
    }


    /** Column classes */

    ._col {
        &.spaced {
            border: @grid-spaced solid transparent;
        }
        &.spaced-top {
            border-top: @grid-spaced solid transparent;
        }
        &.spaced-bottom {
            border-bottom: @grid-spaced solid transparent;
        }
        &.spaced-left {
            border-left: @grid-spaced solid transparent;
        }
        &.spaced-right {
            border-right: @grid-spaced solid transparent;
        }
    }
    [dir=ltr] ._col,
    [dir=rtl] [dir=ltr] ._col {
        &.spaced-start {
            border-left: @grid-spaced solid transparent;
        }
        &.spaced-end {
            border-right: @grid-spaced solid transparent;
        }
    }
    [dir=rtl] ._col,
    [dir=ltr] [dir=rtl] ._col {
        &.spaced-start {
            border-right: @grid-spaced solid transparent;
        }
        &.spaced-end {
            border-left: @grid-spaced solid transparent;
        }
    }
</style>

<docs>
    Columns are the places where content is placed in grid layout. Each ib-row is divided into 12 virtual width units; columns are given
    their width based on how many of these virtual units you want the column to occupy.
    You can nest rows and columns as deeply as you wish. This behavior has many
    similarities to Bootstrap.

    Depending of screen resolution and columns configuration,
    columns could be collapsed - occupies all of the containing row's width, or expanded - columns stack horizontally in ib-row.

    #### Column Modifiers
    There are some modifier classes that can alter the behavior of columns:

    ###### spaced

    Add an 8px space around the column, causing it to appear as an island. Generally
    you will only apply the `spaced` class to your content columns (the ones with no
    child rows). To apply spacing only to selected sides, use any permutation of
    `spaced-top`, `spaced-bottom`, `spaced-left`, and `spaced-right`.


    A flex column is simply a column with the class "flex" attached to it. A column is of
    fixed height if, for example, it is the direct child of a container that is 100%
    of body height. A nested column can also be considered fixed height if it is
    connected to the fixed height container through parents that are all flex columns
    and grow rows. As long as the chain isn't broken by a non-flex column or a non-grow
    row, you will be able to place a grow row in your column and all the parents will
    expand to use exactly the available space in turn.

    Refer to [Grid Docs](/#!/Styles?id=grid) for overall grid layout information
</docs>
